import React, { useEffect, useRef, useState, useCallback } from "react";
import { DrishtiIconMini } from "./DrishtiLogo";

/* ══════════════════════════════════════════════════════════════════
   CAPEXARA COGNITIVE SUBSTRATE — Homepage Cognitive Spine Illustration
   Seven product cards on a Cognara cognitive foundation.
   Query pulses flow DOWN, response pulses flow UP.
   Pure SVG + CSS keyframes + React state (no Three.js)
   ══════════════════════════════════════════════════════════════════ */

const W = 1040, H = 480;
const CARD_Y = 10, CARD_H = 150, CARD_W = 118;
const BAND2_Y = 172, BAND2_H = 68;
const SUB_Y = 240, SUB_H = 230;
const MARGIN = 42;
const SLOT_W = (W - 2 * MARGIN) / 7;

const PRODUCTS = [
  { id: "vidhana",  indo: "Luminex",   dev: "",   cat: "BPM &\nREENGINEERING",            cadence: 4500 },
  { id: "pariksa",  indo: "Assayo",    dev: "",   cat: "MATURITY\nDIAGNOSTICS",           cadence: 7000 },
  { id: "nirnaya",  indo: "Evalore",   dev: "",   cat: "PLATFORM\nEVALUATION",            cadence: 6000 },
  { id: "grantha",  indo: "Docuweave", dev: "",   cat: "ENTERPRISE\nDMS",                 cadence: 3500 },
  { id: "rachana",  indo: "Formigen",  dev: "",   cat: "ENGINEERING\nDESIGN",             cadence: 6500 },
  { id: "drishti",  indo: "Panorix",   dev: "",   cat: "CAPITAL PROJECTS\nCONTROL TOWER", cadence: 2500 },
  { id: "samgraha", indo: "ProcureX",  dev: "",   cat: "PROCUREMENT &\nCONTRACTS",        cadence: 5000 },
];

export default function CapexaraCognitiveSubstrate() {
  const [pulses, setPulses] = useState([]);
  const [queryCount, setQueryCount] = useState(847);
  const [cardStates, setCardStates] = useState(new Array(7).fill("idle"));
  const timersRef = useRef([]);
  const pulseIdRef = useRef(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const h = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  // Pulse orchestration
  const sendQuery = useCallback((idx) => {
    const id = ++pulseIdRef.current;
    const x = MARGIN + SLOT_W * idx + SLOT_W / 2;

    // Card enters QUERYING state
    setCardStates(prev => { const n = [...prev]; n[idx] = "querying"; return n; });
    setPulses(prev => [...prev, { id, idx, x, dir: "down", startTime: Date.now() }]);

    // Reset card state after 400ms
    setTimeout(() => setCardStates(prev => { const n = [...prev]; if (n[idx] === "querying") n[idx] = "idle"; return n; }), 400);

    // Remove down pulse after 600ms, add reasoning delay, then send response up
    setTimeout(() => {
      setPulses(prev => prev.filter(p => p.id !== id));
    }, 600);

    // Response after reasoning delay
    const responseId = id + 1000;
    setTimeout(() => {
      setPulses(prev => [...prev, { id: responseId, idx, x, dir: "up", startTime: Date.now() }]);
      setQueryCount(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 2200);

    // Card RESPONDING when response arrives
    setTimeout(() => {
      setCardStates(prev => { const n = [...prev]; n[idx] = "responding"; return n; });
      setPulses(prev => prev.filter(p => p.id !== responseId));
    }, 2800);

    // Reset to idle
    setTimeout(() => {
      setCardStates(prev => { const n = [...prev]; if (n[idx] === "responding") n[idx] = "idle"; return n; });
    }, 3400);
  }, []);

  useEffect(() => {
    if (reduceMotion) return;
    // Start staggered query timers for each product
    PRODUCTS.forEach((p, i) => {
      const startDelay = i * 600 + Math.random() * 1000;
      const timer = setTimeout(() => {
        sendQuery(i);
        const interval = setInterval(() => sendQuery(i), p.cadence + Math.random() * 1000);
        timersRef.current.push(interval);
      }, startDelay);
      timersRef.current.push(timer);
    });
    return () => timersRef.current.forEach(t => clearTimeout(t));
  }, [sendQuery, reduceMotion]);

  return (
    <div className="ccs-wrap" data-testid="cognitive-substrate">
      <svg viewBox={`0 0 ${W} ${H}`} className="ccs-svg" aria-label="Capexara Cognitive Substrate: seven products powered by Cognara AI foundation">
        <defs>
          <linearGradient id="ccs-sub-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" className="ccs-sub-stop1" />
            <stop offset="100%" className="ccs-sub-stop2" />
          </linearGradient>
          <filter id="ccs-glow">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
          </filter>
        </defs>

        {/* ── Band 3: Substrate ── */}
        <SubstrateLayer queryCount={queryCount} reduceMotion={reduceMotion} />

        {/* ── Band 2: Connections ── */}
        {PRODUCTS.map((_, i) => {
          const x = MARGIN + SLOT_W * i + SLOT_W / 2;
          return <ConnectionChannel key={i} x={x} />;
        })}

        {/* ── Pulses ── */}
        {!reduceMotion && pulses.map(p => <Pulse key={p.id} pulse={p} />)}

        {/* ── Band 1: Product Cards ── */}
        {PRODUCTS.map((p, i) => (
          <ProductCard key={p.id} product={p} idx={i} state={cardStates[i]} />
        ))}
      </svg>
    </div>
  );
}

/* ── Product Card ── */
function ProductCard({ product, idx, state }) {
  const x = MARGIN + SLOT_W * idx + (SLOT_W - CARD_W) / 2;
  const y = CARD_Y;
  const isQuerying = state === "querying";
  const isResponding = state === "responding";

  return (
    <g className={`ccs-card ccs-card-${state}`} data-testid={`ccs-card-${product.id}`}>
      <rect x={x} y={y} width={CARD_W} height={CARD_H} rx={8}
        className={`ccs-card-bg ${isQuerying ? "ccs-card-querying" : ""} ${isResponding ? "ccs-card-responding" : ""}`} />

      {/* Product icon placeholder — distinct per product */}
      <ProductIcon id={product.id} x={x + 12} y={y + 14} />

      {/* Product name */}
      <text x={x + 12} y={y + 58} className="ccs-card-indo">{product.indo}</text>

      {/* Divider */}
      <line x1={x + 12} y1={y + 80} x2={x + CARD_W - 12} y2={y + 80} className="ccs-card-div" />

      {/* Category */}
      {product.cat.split("\n").map((line, j) => (
        <text key={j} x={x + 12} y={y + 96 + j * 12} className="ccs-card-cat">{line}</text>
      ))}

      {/* Activity bar */}
      <rect x={x + 12} y={y + CARD_H - 16} width={CARD_W - 24} height={4} rx={2} className="ccs-bar-track" />
      <rect x={x + 12} y={y + CARD_H - 16} width={(CARD_W - 24) * 0.4} height={4} rx={2}
        className={`ccs-bar-fill ${isResponding ? "ccs-bar-spike" : ""}`}
        style={{ animationDelay: `${idx * 1.2}s` }} />

      {/* Querying spark */}
      {isQuerying && <text x={x + CARD_W - 16} y={y + 16} className="ccs-card-spark">{"\u26a1"}</text>}
    </g>
  );
}

/* ── Product Icons (unique per product) ── */
function ProductIcon({ id, x, y }) {
  const s = 22; // icon size
  return (
    <g className="ccs-icon" transform={`translate(${x},${y})`}>
      {id === "vidhana" && (
        /* BPMN decision diamond with 4 exit lines + orange center */
        <g><rect x={4} y={4} width={14} height={14} rx={2} fill="none" strokeWidth="1.3" transform={`rotate(45 ${11} ${11})`} />
        <line x1={11} y1={0} x2={11} y2={3} strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
        <line x1={11} y1={19} x2={11} y2={22} strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
        <line x1={0} y1={11} x2={3} y2={11} strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
        <line x1={19} y1={11} x2={22} y2={11} strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
        <circle cx={11} cy={11} r={2} fill="#F26B1F" stroke="none" /></g>
      )}
      {id === "pariksa" && (
        /* 3-bar calibration stack */
        <g><rect x={0} y={3} width={s*0.6} height={3} rx={1} /><rect x={0} y={10} width={s*0.75} height={3} rx={1} /><rect x={0} y={17} width={s*0.5} height={3} rx={1} /></g>
      )}
      {id === "nirnaya" && (
        /* 3×3 evaluation grid (matches product page logo) */
        <g>{[0,1,2].map(r => [0,1,2].map(c => <rect key={`${r}${c}`} x={c*8} y={r*8} width={6} height={6} rx={1} opacity={r===0&&c===2 ? 1 : 0.5} fill={r===0&&c===2 ? "#E8652D" : undefined} />))}</g>
      )}
      {id === "grantha" && (
        /* 3 isometric stacked document layers + orange AI-spark (matches master logo) */
        <g transform="translate(1,1) scale(0.42)">
          <path d="M0 38 L24 28 L48 36 L24 46 Z" fill="#3A3F4A" opacity="0.75" stroke="none" />
          <path d="M0 26 L24 16 L48 24 L24 34 Z" fill="#1E4E8C" opacity="0.9" stroke="none" />
          <path d="M0 14 L24 4 L48 12 L24 22 Z" fill="#2E70AD" stroke="none" />
          <path d="M41 -7 L42.8 -1.8 L48 0 L42.8 1.8 L41 7 L39.2 1.8 L34 0 L39.2 -1.8 Z" fill="#F26B1F" stroke="none" />
        </g>
      )}
      {id === "rachana" && (
        /* Distillation vessel with nozzle + orange dot */
        <g><path d={`M7,0 L15,0 L15,6 L7,6 Z`} fill="none" strokeWidth="1.3" />
        <path d={`M4,6 L4,16 Q4,${s} ${s/2},${s} Q${s-4},${s} ${s-4},16 L${s-4},6`} fill="none" strokeWidth="1.4" />
        <line x1={4} y1={6} x2={s-4} y2={6} strokeWidth="1.3" />
        <line x1={0} y1={3} x2={7} y2={3} strokeWidth="1.2" strokeLinecap="round" />
        <circle cx={s/2} cy={14} r={2.5} fill="#F26B1F" stroke="none" /></g>
      )}
      {id === "drishti" && (
        /* Hex-network mini (matches master Panorix logo) */
        <DrishtiIconMini size={22} stroke="currentColor" color="#F26B1F" />
      )}
      {id === "samgraha" && (
        /* River Delta mini — trunk splits into 3 branches + orange spark at source */
        <g transform="translate(1, 1) scale(0.34)">
          <path d="M30 4 L30 20" stroke="currentColor" strokeWidth="4.5" strokeLinecap="round" fill="none" />
          <path d="M30 20 Q28 30, 12 44" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" fill="none" opacity="0.85" />
          <path d="M30 20 Q30 32, 30 46" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" opacity="0.75" />
          <path d="M30 20 Q32 30, 48 44" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.65" />
          <circle cx="30" cy="18" r="5" fill="#F26B1F" />
        </g>
      )}
    </g>
  );
}

/* ── Connection Channel ── */
function ConnectionChannel({ x }) {
  return (
    <g className="ccs-channel" aria-hidden="true">
      <line x1={x} y1={CARD_Y + CARD_H} x2={x} y2={SUB_Y} className="ccs-chan-line" />
    </g>
  );
}

/* ── Pulse (query down / response up) — CSS-animated ── */
function Pulse({ pulse }) {
  const y1 = CARD_Y + CARD_H;
  const dirClass = pulse.dir === "down" ? "ccs-pulse-anim-down" : "ccs-pulse-anim-up";

  return (
    <g className={`ccs-pulse ${dirClass}`} aria-hidden="true"
       style={{ "--pulse-x": pulse.x }}>
      <rect x={pulse.x - 6} y={y1} width={12} height={4} rx={2}
        className={pulse.dir === "down" ? "ccs-pulse-down" : "ccs-pulse-up"} />
      {pulse.dir === "up" && (
        <text x={pulse.x - 6} y={y1 + 1} className="ccs-pulse-spark">{"\u26a1"}</text>
      )}
      {[1, 2, 3].map(i => (
        <rect key={i} x={pulse.x - (5 - i)} y={y1 + (pulse.dir === "down" ? -i * 5 : i * 5)} width={10 - i * 2} height={2} rx={1}
          className="ccs-pulse-tail" style={{ opacity: 0.7 - i * 0.2 }} />
      ))}
    </g>
  );
}

/* ── Substrate Layer ── */
function SubstrateLayer({ queryCount, reduceMotion }) {
  return (
    <g className="ccs-substrate">
      {/* Background */}
      <rect x={20} y={SUB_Y} width={W - 40} height={SUB_H} rx={12}
        fill="url(#ccs-sub-grad)" className="ccs-sub-bg" />
      {/* Top border only */}
      <line x1={32} y1={SUB_Y} x2={W - 32} y2={SUB_Y} className="ccs-sub-top-border" />

      {/* Cognara Axis Mundi Icon + Label */}
      <g transform={`translate(38, ${SUB_Y + 8})`}>
        <line x1="0" y1="6" x2="24" y2="6" stroke="#D4D7DE" strokeWidth="1.5" strokeLinecap="round" opacity="0.9" className="sd-logo-hline" />
        <line x1="0" y1="12" x2="24" y2="12" stroke="#D4D7DE" strokeWidth="1.5" strokeLinecap="round" opacity="0.75" className="sd-logo-hline" />
        <line x1="0" y1="18" x2="24" y2="18" stroke="#D4D7DE" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" className="sd-logo-hline" />
        <line x1="12" y1="1" x2="12" y2="23" stroke="#F26B1F" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="6" r="2" fill="#F26B1F" />
        <circle cx="12" cy="12" r="2" fill="#F26B1F" />
        <circle cx="12" cy="18" r="2" fill="#F26B1F" />
      </g>
      <text x={72} y={SUB_Y + 28} className="ccs-sub-label">{"Cognara · AI Foundation"}</text>
      <text x={48} y={SUB_Y + 44} className="ccs-sub-meta">{"COGNITIVE SUBSTRATE \u00b7 9 AGENTS \u00b7 2.4B TOKENS"}</text>

      {/* Throughput */}
      <text x={W - 48} y={SUB_Y + 28} textAnchor="end" className="ccs-sub-throughput">{queryCount} QUERIES/MIN</text>

      {/* Token streams (Type A) */}
      {!reduceMotion && Array.from({ length: 35 }).map((_, i) => {
        const sy = SUB_Y + 55 + (i % 10) * 17 + Math.sin(i * 0.7) * 8;
        const sw = 4 + Math.random() * 6;
        return (
          <line key={`ts-${i}`} x1={40} y1={sy} x2={40 + sw} y2={sy}
            className="ccs-token-stream"
            style={{ animationDelay: `${i * 0.3}s`, animationDuration: `${8 + i % 5 * 2}s` }} />
        );
      })}

      {/* Knowledge nodes (Type B) */}
      {Array.from({ length: 40 }).map((_, i) => {
        const nx = 60 + (i * 23.7) % (W - 120);
        const ny = SUB_Y + 60 + (i * 17.3) % (SUB_H - 80);
        return (
          <circle key={`kn-${i}`} cx={nx} cy={ny} r={2}
            className="ccs-know-node"
            style={{ animationDelay: `${i * 0.4}s` }} />
        );
      })}

      {/* Node connections */}
      {Array.from({ length: 15 }).map((_, i) => {
        const x1 = 60 + (i * 47) % (W - 120);
        const y1 = SUB_Y + 65 + (i * 29) % (SUB_H - 90);
        const x2 = x1 + 30 + (i % 3) * 20;
        const y2 = y1 + (i % 2 === 0 ? 15 : -10);
        return <line key={`nc-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} className="ccs-node-conn" />;
      })}

      {/* Reasoning chains (Type C) — 2 persistent chains */}
      {!reduceMotion && [0, 1].map(i => {
        const startX = 120 + i * 400;
        const startY = SUB_Y + 100 + i * 40;
        return (
          <path key={`rc-${i}`}
            d={`M${startX},${startY} Q${startX + 40},${startY - 20} ${startX + 80},${startY + 10} T${startX + 140},${startY - 5}`}
            className="ccs-reason-chain"
            style={{ animationDelay: `${i * 4 + 1}s` }} />
        );
      })}

      {/* Bottom verb: "One brain. Seven expressions." */}
      <text x={W / 2} y={SUB_Y + SUB_H - 12} textAnchor="middle" className="ccs-sub-verb">
        {"ONE BRAIN \u00b7 SEVEN EXPRESSIONS"}
      </text>
    </g>
  );
}
